import { NgIf, NgFor, NgClass, UpperCasePipe } from "@angular/common";
import { Component, Input, Output, EventEmitter, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { MAX_FILE_SIZE } from "src/app/constants/file.constants";
import { PortalToBodyDirective } from "src/app/directives/portal-to-body.directive";
import { AddCatalogItemMedia } from "src/app/models/catalogs/catalog";
import { environment } from "src/environments/environment";
import Swal from "sweetalert2";

@Component({
  selector: "app-multimedia-step",
  standalone: true,
  templateUrl: "./multimedia-step.component.html",
  styleUrls: ["./multimedia-step.component.css"],
  imports: [NgIf, NgFor, NgClass, FormsModule, PortalToBodyDirective, UpperCasePipe],
})
export class MultimediaStepComponent implements OnInit {
  @Input() public isReadonly: boolean = false;
  @Input() data: any = {};
  @Output() dataChange = new EventEmitter<any>();

  public readonly sanitizer = inject(DomSanitizer);

  coverFile: string | null = null;
  coverFileId: number | null = null;
  coverPreview: string | null = null;
  galleryItems: AddCatalogItemMedia[] = [];

  MAX_FILE_SIZE = MAX_FILE_SIZE;
  ALLOWED_COVER_TYPES = ["image/png", "image/jpeg", "image/webp"];
  ALLOWED_GALLERY_TYPES = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "video/mp4",
    "video/webm",
    "video/ogg",
    "application/pdf",
  ];
  GALLERY_ACCEPT = this.ALLOWED_GALLERY_TYPES.join(",");

  pdfPreviewSrc: string | null = null;
  pdfPreviewSafe: SafeResourceUrl | null = null;
  pdfPreviewName = "";

  fileBaseUrl = `${environment.api.url}/File/ViewFile`;

  ngOnInit(): void {
    this.coverFile = this.data?.coverFile ?? null;
    this.coverFileId = this.data?.coverFileId ?? null;
    this.coverPreview = this.data?.coverPreview ?? this.coverFile ?? null;
    this.galleryItems = Array.isArray(this.data?.gallery) ? [...this.data.gallery] : [];
    this.galleryItems.forEach((g: AddCatalogItemMedia, i: number) => {
      g.sortOrder ??= i + 1;
      g.filePreview ??= g.fileBase64;
    });
  }

  triggerCoverInput() {
    const el = document.getElementById("coverFileInput") as HTMLInputElement | null;
    el?.click();
  }

  async onCoverImageSelect(event: any) {
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) return;
    if (file.size > this.MAX_FILE_SIZE) {
      Swal.fire({ icon: "error", title: "Archivo demasiado grande", text: `El archivo excede ${this.MAX_FILE_SIZE / 1024 / 1024} MB.` });
      event.target.value = "";
      return;
    }
    if (!this.ALLOWED_COVER_TYPES.includes(file.type)) {
      Swal.fire({ icon: "warning", title: "Formato no válido", text: "Solo se permiten PNG, JPG o WEBP." });
      event.target.value = "";
      return;
    }
    this.coverPreview = URL.createObjectURL(file);
    this.coverFile = await this.readFileAsDataURL(file);
    this.coverFileId = null;
    this.updateData();
  }

  triggerGalleryInput() {
    const el = document.getElementById("galleryFileInput") as HTMLInputElement | null;
    el?.click();
  }

  async onGalleryFileSelected(event: any) {
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) return;
    event.target.value = "";
    if (file.size > this.MAX_FILE_SIZE) {
      Swal.fire({ icon: "error", title: "Archivo demasiado grande", text: `El archivo excede ${this.MAX_FILE_SIZE / 1024 / 1024} MB.` });
      return;
    }
    if (!this.ALLOWED_GALLERY_TYPES.includes(file.type)) {
      Swal.fire({ icon: "warning", title: "Tipo no permitido", html: `Tipos permitidos:<br><b>Imágenes:</b> PNG, JPG, WEBP<br><b>Videos:</b> MP4, WEBM, OGG<br><b>PDF:</b> application/pdf` });
      return;
    }
    const base64 = await this.readFileAsDataURL(file);
    const preview = URL.createObjectURL(file);
    const mediaType = this.resolveMediaType(file.type);
    const item: AddCatalogItemMedia = {
      id: null,
      versionId: this.data?.versionId ?? 0,
      mediaType,
      caption: "",
      sortOrder: this.galleryItems.length + 1,
      isEnabled: true,
      fileBase64: base64,
      filePreview: preview,
      fileName: file.name,
      contentType: file.type,
    };
    this.galleryItems = [...this.galleryItems, item];
    this.updateData();
  }

  resolveMediaType(mime: string): string {
    if (mime.startsWith("image/")) return "IMAGE";
    if (mime.startsWith("video/")) return "VIDEO";
    return "PDF";
  }

  readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  }

  onCaptionChange() {
    this.updateData();
  }

  toggleEnabled(item: AddCatalogItemMedia) {
    item.isEnabled = !item.isEnabled;
    this.updateData();
  }

  removeItem(index: number) {
    const item = this.galleryItems[index];
    if (item.filePreview?.startsWith('blob:')) URL.revokeObjectURL(item.filePreview);
    this.galleryItems.splice(index, 1);
    this.galleryItems.forEach((g, i) => (g.sortOrder = i + 1));
    this.galleryItems = [...this.galleryItems];
    this.updateData();
  }

  moveUp(index: number) {
    if (index <= 0) return;
    [this.galleryItems[index - 1], this.galleryItems[index]] = [this.galleryItems[index], this.galleryItems[index - 1]];
    this.galleryItems.forEach((g, i) => (g.sortOrder = i + 1));
    this.galleryItems = [...this.galleryItems];
    this.updateData();
  }

  moveDown(index: number) {
    if (index >= this.galleryItems.length - 1) return;
    [this.galleryItems[index + 1], this.galleryItems[index]] = [this.galleryItems[index], this.galleryItems[index + 1]];
    this.galleryItems.forEach((g, i) => (g.sortOrder = i + 1));
    this.galleryItems = [...this.galleryItems];
    this.updateData();
  }

  openPdf(item: AddCatalogItemMedia) {
    const src = item?.fileId ? `${this.fileBaseUrl}/${item.fileId}` : item.filePreview || item.fileBase64;
    this.pdfPreviewSrc = src;
    this.pdfPreviewSafe = src ? this.sanitizer.bypassSecurityTrustResourceUrl(src) : null;
    this.pdfPreviewName = item.fileName || "Documento.pdf";
    const modal = new (window as any).bootstrap.Modal('#pdfModal', {});
    modal.show();
  }

  updateData() {
    const updatedData = {
      ...this.data,
      coverFile: this.coverFile,
      coverPreview: this.coverPreview,
      gallery: this.galleryItems
    };
    this.dataChange.emit(updatedData);
  }

  playVideo(vid: HTMLVideoElement, overlay: HTMLElement, ev?: Event) {
    ev?.stopPropagation();
    overlay.style.display = "none";
    vid.setAttribute("controls", "true");
    vid.muted = false;
    vid.play();
  }

  hideOverlay(overlay: HTMLElement) {
    overlay.style.display = "none";
  }

  showOverlay(overlay: HTMLElement) {
    overlay.style.display = "flex";
  }

  validate() {
    if (!this.coverFile && !this.coverFileId) {
      Swal.fire({
        icon: "warning",
        title: "Portada requerida",
        text: "Debes seleccionar una imagen de portada antes de continuar.",
      });
      return false;
    }
    return true;
  }
}